import { useEffect, useMemo } from "react"
import { useController, useFormContext } from "react-hook-form"
import { useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import type { InputYearMonthProps, InputYearMonthValue } from "./InputYearMonth"
import { InputYearMonth } from "./InputYearMonth"

export interface InputYearMonthFieldProps extends Omit<InputYearMonthProps, "value" | "onChange"> {
	name: string
}

export const InputYearMonthField = (props: InputYearMonthFieldProps) => {
	const { name, required, ...rest } = props

	const { formatMessage } = useIntl()
	const { control, trigger } = useFormContext()

	const rules = useMemo(() => {
		return {
			required: {
				message: formatMessage(formMessages.required),
				value: !!required,
			},
		}
	}, [required, formatMessage])

	const {
		field: { onChange, value },
		fieldState: { error, isDirty },
		formState: { isSubmitted },
	} = useController({
		control,
		name,
		rules,
		shouldUnregister: true,
	})

	// Fields need to retrigger their validation when the field is either dirty or the form is submitted.
	// See https://github.com/react-hook-form/react-hook-form/issues/1749 and https://github.com/react-hook-form/react-hook-form/issues/8310
	useEffect(() => {
		if (isDirty || isSubmitted) {
			trigger(name)
		}
	}, [rules, name, trigger, isDirty, isSubmitted])

	const handleChange = (newValue: InputYearMonthValue) => onChange(!newValue ? null : newValue)

	return <InputYearMonth {...rest} name={name} onChange={handleChange} errorMessage={error?.message} value={value} />
}
